import WithLoadingSpinner from 'components/withLoadingSpinner/WithLoadingSpinner';
import { useAgenciesQuery } from 'core/graphql/Agency/Agency.generated';
import {
  UpdateAgencySettingsMutation,
  useUpdateAgencySettingsMutation,
} from 'core/graphql/Settings/UpdateAgencySettings.generated';
import {
  UpdateCourierSettingsMutation,
  useUpdateCourierSettingsMutation,
} from 'core/graphql/Settings/UpdateCourierSettings.generated';
import { toast } from 'react-toastify';
import useUrl from 'core/hooks/useUrl';
import DesignSelect from 'components/select/DesignSelect';
import EditableSettingsCard from 'components/editableSettingsCard/EditableSettingsCard';
import useAvailableProvidersDataStore from 'core/hooks/useAvailableProviderSettingsDataStore';
import useAgencySettingsDataStore from '../../core/hooks/useAgencySettingsDataStore';
import { withAuthenticationRequired } from 'react-oidc-context';

const Settings = () => {
  const { paramValue: agencyId, setParamValue: setAgencyId } =
    useUrl<string>('Agency');
  const { data, loading: areAgenciesLoading } = useAgenciesQuery();
  const { agencySettings } = useAgencySettingsDataStore(agencyId!);
  const [updateCourierSettingsMutation, { loading: updateCourierLoading }] =
    useUpdateCourierSettingsMutation({
      refetchQueries: ['AgencySettings'],
    });

  const [updateAgencySettingsMutation, { loading: updateAgencyLoading }] =
    useUpdateAgencySettingsMutation({
      refetchQueries: ['AgencySettings'],
    });

  const availableProvidersResult = useAvailableProvidersDataStore();

  const handleAgencyChanged = ({ value }: any) => {
    setAgencyId(value);
  };

  const onCompletedCourierUpdate = (
    response: UpdateCourierSettingsMutation,
  ) => {
    if (response.UpdateCourierSettings.errors?.length) {
      const erroMessages = response.UpdateCourierSettings.errors
        .map((error) => error.message)
        .join('\n');
      toast.error(erroMessages);
    }
  };

  const handleCourierUpdate = (
    id: number,
    etaValue: string | undefined,
    defaultItineraryProvider: string | null | undefined,
    courierDesignedItineraryProvider: string | null | undefined,
  ) => {
    updateCourierSettingsMutation({
      variables: {
        input: {
          courierSettingsMutationInput: {
            courierId: id,
            etaInterval: Number(etaValue),
            courierDesignProvider: courierDesignedItineraryProvider,
            defaultProvider: defaultItineraryProvider,
          },
        },
      },
      onCompleted: onCompletedCourierUpdate,
    });
  };

  const onCompletedAgencyrUpdate = (response: UpdateAgencySettingsMutation) => {
    if (response.UpdateAgencySettings.errors?.length) {
      const erroMessages = response.UpdateAgencySettings.errors
        .map((error) => error.message)
        .join('\n');
      toast.error(erroMessages);
    }
  };

  const handleAgencyUpdate = (
    id: any,
    etaValue: string | undefined,
    defaultItineraryProvider: string | null | undefined,
    courierDesignedProvider: string | null | undefined,
  ) => {
    updateAgencySettingsMutation({
      variables: {
        input: {
          agencySettingsMutationInput: {
            agencyId: id,
            etaInterval: Number(etaValue),
            courierDesignProvider: courierDesignedProvider,
            defaultProvider: defaultItineraryProvider,
          },
        },
      },
      onCompleted: onCompletedAgencyrUpdate,
    });
  };

  const agencies = data?.Agencies.map((x) => ({ value: x.id, label: x.name }));
  const agency = agencies?.find((x) => x.value === agencyId);
  const loading =
    areAgenciesLoading || updateCourierLoading || updateAgencyLoading;

  return (
    <WithLoadingSpinner isLoading={loading}>
      <div className="flex h-full dark:bg-design-black dark:text-white">
        <div className="mx-8 mt-8 flex w-1/4 flex-col pr-2">
          <label htmlFor="agencySelect">Select agency</label>
          <DesignSelect
            isLoading={areAgenciesLoading}
            value={agency}
            onChange={handleAgencyChanged}
            options={agencies}
            isMulti={false}
            closeMenuOnSelect
          />
        </div>
        <div className="max-h-screen w-3/4  overflow-y-auto">
          <div className="container mx-auto pt-8">
            {agencySettings && (
              <div className="m-4">
                <h3 className="text-xl font-bold">Agency Settings</h3>
                <p>
                  These settings affect all the couriers from the selected
                  agency
                </p>
                <div className="mt-2 flex justify-between p-2">
                  <div>{`${agencySettings.name}`}</div>
                  <EditableSettingsCard
                    entityId={agencyId}
                    entity={agencySettings}
                    availableDefaultProviders={
                      availableProvidersResult.defaultItineraryProviders
                    }
                    availableCourierDesignedProviders={
                      availableProvidersResult.courierDesignedItineraryProviders
                    }
                    providersAreLoading={
                      availableProvidersResult.areProvidersLoading
                    }
                    onSave={handleAgencyUpdate}
                  />
                </div>
                <h3 className="text-xl font-bold">Courier Settings</h3>
                <p>These settings affect the courier individually</p>

                {agencySettings.couriers &&
                  agencySettings.couriers.map((item) => (
                    <div
                      key={item.courierId}
                      className="border-gray-200 mt-2 flex justify-between border-b p-2 last:border-none"
                    >
                      <div>
                        <div>{`${item.indicative}`}</div>
                        <div className="text-xs">{`Courier Id ${item.courierId}`}</div>
                      </div>
                      <EditableSettingsCard
                        entityId={item.courierId}
                        entity={item}
                        availableDefaultProviders={
                          availableProvidersResult.defaultItineraryProviders
                        }
                        availableCourierDesignedProviders={
                          availableProvidersResult.courierDesignedItineraryProviders
                        }
                        providersAreLoading={
                          availableProvidersResult.areProvidersLoading
                        }
                        onSave={handleCourierUpdate}
                      />
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </WithLoadingSpinner>
  );
};

export default withAuthenticationRequired(Settings);
